import React, { useState, useEffect } from "react";
import JobPostModal from "./JobPostModal";
import JobPostDetails from "./JobPostDetails";
import PayForJobPostResults from './PayForJobPostResults';
import { api } from "../shared/axiosInstance";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from '../shared/stripe';
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import OnboardingChecklist from "./OnboardingChecklist";

const JobPostsList = ({
                          jobPosts = [],
                          refreshJobPosts,
                          userId,
                          totalJobCount = 0,
                          onShowWizard
                      }) => {
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [editingJob, setEditingJob] = useState(null);
    const [isAddingJob, setIsAddingJob] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [jobCounts, setJobCounts] = useState({});

    const isUnlocked = (job) => {
        if (!job.lastPaidDate) return false;

        const paidDate = new Date(job.lastPaidDate);
        const expirationDate = new Date(paidDate);
        expirationDate.setDate(expirationDate.getDate() + 30);
        expirationDate.setUTCHours(6, 0, 0, 0); // 10 PM PST (6 AM UTC next day)

        return expirationDate > new Date();
    };

    useEffect(() => {
        const fetchJobCounts = async () => {
            const counts = {};

            for (const job of jobPosts) {
                try {
                    const response = await api.get(`/matches/${job._id}/count`);
                    counts[job._id] = response.data;
                } catch (error) {
                    console.error(`❌ Failed to load counts for job ${job._id}:`, error.response?.data || error);
                }
            }

            setJobCounts(counts);
        };

        if (jobPosts.length > 0) {
            fetchJobCounts();
        }
    }, [jobPosts]);

    const handleExpand = async (job) => {
        // If no targets exist, prompt to add targets
        if (!job.targets || job.targets.length === 0) {
            setEditingJob(job); // Open edit modal
            return;
        }

        const pendingTarget = job.targets.find(t => t.status !== "ready");

        if (pendingTarget) {
            // Notify incomplete target
            try {
                const response = await api.post(`/jobs/${job._id}/notify-target/${pendingTarget.email}`);
                alert(response.data.message || `Reminder sent to ${pendingTarget.firstName}`);
                refreshJobPosts();
            } catch (error) {
                console.error("Error sending reminder:", error);
                alert("Failed to send reminder.");
            }
        } else if (!isUnlocked(job)) {
            setModalContent({ type: "unlock", job });
        } else {
            setExpandedJobId(expandedJobId === job._id ? null : job._id);
        }
    };




    const handleEdit = (job) => {
        setEditingJob(job);
    };

    const handleAddJob = () => {
        setIsAddingJob(true);
    };

    const handleSaveJobPost = () => {
        // Once the modal returns the updated job data, refresh the job posts
        refreshJobPosts();
        setIsAddingJob(false);
        setEditingJob(null);
    };

    return (

        <div className="bg-bluegray shadow-md p-4 rounded-lg w-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-white">Job Posts</h2>
                <button
                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                    onClick={handleAddJob}
                >
                    + Add Job
                </button>
            </div>

            {jobPosts.length === 0 && totalJobCount === 0 ? (
                <OnboardingChecklist
                    onPostJob={handleAddJob}
                    userId={userId}
                    onShowWizard={onShowWizard}
                />
            ) : jobPosts.length === 0 ? (
                <p className="text-white text-center">No job posts match your filters.</p>
            ) : (
                <ul className="space-y-4">
                    {jobPosts.map((job) => (
                        <li key={job._id} className="bg-darkblue p-4 rounded-md shadow-lg">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-white">{job.title}</h3>
                                    <p className="text-sm text-gray-400">Status: {job.status}</p>
                                    {jobCounts[job._id] && (
                                        <div className="text-sm text-lightgray mt-2">
                                            <div>
                                                Matches: {jobCounts[job._id].total || 0} —
                                                <span className="text-green-400 font-bold ml-1">Perfect: {jobCounts[job._id].perfect || 0}</span> |
                                                <span className="text-blue-400 font-bold ml-1">Great: {jobCounts[job._id].great || 0}</span> |
                                                <span className="text-yellow-400 font-bold ml-1">Potential: {jobCounts[job._id].potential || 0}</span>
                                            </div>
                                            <div className="mt-1">
                                                Applied: {jobCounts[job._id].appliedTotal || 0} —
                                                <span className="text-green-400 font-bold ml-1">Perfect: {jobCounts[job._id].appliedPerfect || 0}</span> |
                                                <span className="text-blue-400 font-bold ml-1">Great: {jobCounts[job._id].appliedGreat || 0}</span> |
                                                <span className="text-yellow-400 font-bold ml-1">Potential: {jobCounts[job._id].appliedPotential || 0}</span> |
                                                <span className="text-gray-400 font-bold ml-1">Other: {jobCounts[job._id].appliedOther || 0}</span>
                                            </div>
                                        </div>

                                    )}
                                </div>

                                {userId && (
                                    <div className="flex flex-col mt-2">
                                        <p className="text-xs text-lightblue mb-1 font-semibold">Candidate Application Link</p>
                                        <p className="text-xs text-gray-400 mb-2">Share this in your response to applicants. Candidates who sign up through it are automatically matched to this job.</p>
                                        <div className="flex items-center">
                                            <input
                                                type="text"
                                                readOnly
                                                value={`careermission.ai/candidate-signup?referredBy=${userId}&jobId=${job._id}`}
                                                className="bg-gray-700 text-white p-1 rounded border border-gray-500 w-72 text-xs"
                                                onClick={(e) => e.target.select()}
                                            />
                                            <button
                                                onClick={() => navigator.clipboard.writeText(`careermission.ai/candidate-signup?referredBy=${userId}&jobId=${job._id}`)}
                                                className="ml-2 p-2 bg-lightblue rounded-lg hover:bg-orange"
                                            >
                                                <ClipboardDocumentIcon className="h-5 w-5 text-darkblue" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="flex space-x-2">
                                    <button
                                        className="bg-orange text-white px-3 py-1 rounded-md hover:bg-deeporange"
                                        onClick={() => handleEdit(job)}
                                    >
                                        Edit Job
                                    </button>

                                    {(() => {
                                        const noTargets = !job.targets || job.targets.length === 0;
                                        const pendingTarget = job.targets?.find(t => t.status !== "ready");
                                        const isExpanded = expandedJobId === job._id;

                                        let buttonLabel = "";
                                        let buttonClasses = "";
                                        if (noTargets) {
                                            buttonLabel = "Add Target";
                                            buttonClasses = "bg-lightblue hover:bg-blue-300 text-darkblue";
                                        } else if (pendingTarget) {
                                            buttonLabel = `Remind ${pendingTarget.firstName}`;
                                            buttonClasses = "bg-yellow-300 hover:bg-yellow-400 text-darkblue";
                                        } else if (!isUnlocked(job)) {
                                            buttonLabel = "Pay to See Results";
                                            buttonClasses = "bg-orange hover:bg-deeporange text-lightgrey";
                                        } else if (isExpanded) {
                                            buttonLabel = "Collapse";
                                            buttonClasses = "bg-blue-600 hover:bg-blue-800 text-lightgrey";
                                        } else {
                                            buttonLabel = "Expand";
                                            buttonClasses = "bg-blue-600 hover:bg-blue-800 text-lightgrey";
                                        }

                                        return (
                                            <button
                                                className={`${buttonClasses} px-3 py-1 rounded-md`}
                                                onClick={() => handleExpand(job)}
                                            >
                                                {buttonLabel}
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>

                            {expandedJobId === job._id && <JobPostDetails job={job} />}
                        </li>
                    ))}
                </ul>
            )}

            {editingJob && (
                <JobPostModal
                    isOpen={!!editingJob}
                    onClose={() => setEditingJob(null)}
                    job={editingJob}
                    onSave={handleSaveJobPost}
                />
            )}

            {isAddingJob && (
                <JobPostModal
                    isOpen={isAddingJob}
                    onClose={() => setIsAddingJob(false)}
                    job={null}
                    onSave={handleSaveJobPost}
                />
            )}
            {modalContent?.type === "unlock" && (
                <Elements stripe={stripePromise}>
                    <PayForJobPostResults
                        job={modalContent.job}
                        onClose={() => setModalContent(null)}
                        refreshJobPosts={refreshJobPosts}
                    />
                </Elements>
            )}
        </div>
    );
};

export default JobPostsList;
